import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import type { UtteranceDto } from "../../lib/api.js";
import type { JakoscWypowiedzi } from "../../lib/jakoscWypowiedzi.js";
import type { UwagaWypowiedzi } from "../../lib/raportOceny.js";
import { mmss } from "./czas.js";
import { zaznaczeniaWTekscie, type ZakresCytatu, type ZakresZaznaczenia } from "./fragmenty.js";
import { LegendaJakosci } from "./LegendaJakosci.js";
import { Wypowiedz } from "./Wypowiedz.js";

/**
 * Ile wypowiedzi pokazujemy naraz. Karta rosnie z trescia i scrolluje sie strona
 * (odstepstwo od CV-1), wiec bez porcjowania 40-minutowa rozmowa to setki dymkow
 * w jednym renderze.
 */
const PORCJA = 40;

/** Od jakiej przerwy miedzy wypowiedziami wstawiamy separator systemowy. */
const PROG_PRZERWY_MS = 15_000;

/** Jak dlugo wyrozniona po przejsciu z karty oceny wypowiedz zostaje wyrozniona. */
const CZAS_WYROZNIENIA_MS = 3000;

interface Props {
  utterances: readonly UtteranceDto[];
  /** Rotacyjny tint tozsamosci (1-4) dla nazwy mowcy. */
  tintDlaMowcy: (speaker: string) => number;
  modelEkspresji: string | null;
  /** Zakresy do podswietlenia, kluczowane indeksem wypowiedzi. */
  zakresy: ReadonlyMap<number, ZakresCytatu[]>;
  /** Indeks wypowiedzi, ktora wlasnie leci z nagrania. `null` = nic nie gra. */
  biezacyIndex: number | null;
  /** `null`, gdy nagrania nie ma - znaczniki czasu przestaja byc kontrolkami. */
  naPrzewin: ((ms: number) => void) | null;
  dlugoscSek: number | null;
  /**
   * False, gdy czasy wypowiedzi nie istnieja (tekst bez znacznikow czasu). Znikaja wtedy znaczniki
   * czasu przy wypowiedziach i separatory "Przerwa N s" - przy startMs rownym 0 wszystkie
   * liczyly by sie od zera i pokazywaly nieprawdziwe przerwy.
   */
  pokazCzas?: boolean;
  /** "tekst": rozmowa nie pochodzi z nagrania, wiec pierwsza linia mowi o poczatku rozmowy, a nie nagrania. */
  rodzajMaterialu?: "nagranie" | "tekst";
  /** Jakosc wypowiedzi (kolor) po indeksie wypowiedzi. Wypowiedz bez wpisu zostaje bez koloru. */
  jakosci?: ReadonlyMap<number, JakoscWypowiedzi>;
  /** Legenda kolorow nad rozmowa: tylko wtedy, gdy jest ocena, z ktorej kolory moga wynikac. */
  pokazLegende?: boolean;
  /** Uwagi z oceny po indeksie wypowiedzi: stoja przy dymku, z ktorego pochodza. Wypowiedz bez wpisu nie ma uwag. */
  uwagi?: ReadonlyMap<number, readonly UwagaWypowiedzi[]>;
  /**
   * Prosba o pokazanie wypowiedzi (przycisk "Pokaz w transkrypcie" w karcie oceny). `token` rosnie przy kazdej
   * prosbie, wiec ponowne klikniecie tej samej wypowiedzi tez przewija, a zwykle odswiezenie widoku - nie.
   * `trafienia`: miejsca powtarzanego slowa w tej wypowiedzi (lista "Powtarzane slowa"); zaznaczamy je na czas
   * wyroznienia i przewijamy do pierwszego z nich, a nie do srodka dymka.
   */
  skokDo?: { index: number; token: number; trafienia?: readonly ZakresZaznaczenia[] } | null;
}

/** Wspolna pusta lista: dymek bez zaznaczen dostaje zawsze te sama referencje. */
const BRAK_ZAZNACZEN: readonly ZakresZaznaczenia[] = [];

function ograniczonyRuch(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Czy po ustawieniu srodka dymka na srodku okna numer i mowca (wiersz nad dymkiem) zostaja ponizej pasa zakrytego u gory
 * okna. Wysokosc tego pasa to scroll-margin-top wypowiedzi (session.css: przypiety naglowek z pasmem wygaszania), wiec ta
 * sama liczba mowi tutaj, czy wysrodkowanie wystarczy, a przegladarce, gdzie postawic poczatek wypowiedzi.
 */
function numerWidacPoWysrodkowaniu(wiersz: HTMLElement, dymek: HTMLElement): boolean {
  const prostokat = dymek.getBoundingClientRect();
  const nadDymkiem = prostokat.top - wiersz.getBoundingClientRect().top;
  const zakryte = Number.parseFloat(window.getComputedStyle(wiersz).scrollMarginTop) || 0;
  return window.innerHeight / 2 - prostokat.height / 2 - nadDymkiem >= zakryte;
}

/**
 * Przewija do wypowiedzi. Zwykle srodek DYMKU staje na srodku okna. Gdy numer i mowca wypadlyby wtedy pod przypietym
 * naglowkiem (dlugi dymek), przewijamy do poczatku calej wypowiedzi, a scroll-margin-top stawia ja tuz pod naglowkiem:
 * srodek dlugiej wypowiedzi (setki slow) to sciana tekstu bez numeru i mowcy na ekranie.
 */
function przewinDoWypowiedzi(wiersz: HTMLElement): void {
  // jsdom nie implementuje scrollIntoView - brak metody nie moze wywrocic widoku.
  if (typeof wiersz.scrollIntoView !== "function") return;
  const behavior = ograniczonyRuch() ? "auto" : "smooth";
  const dymek = wiersz.querySelector<HTMLElement>(".bubble");
  if (dymek && numerWidacPoWysrodkowaniu(wiersz, dymek)) dymek.scrollIntoView({ block: "center", behavior });
  else wiersz.scrollIntoView({ block: "start", behavior });
}

/**
 * Transkrypt jako rozmowa (§9.4).
 *
 * Porcjowanie idzie OD POCZATKU, nie od konca, mimo ze kanoniczny czat doladowuje
 * "starsze" u gory. Powod jest funkcjonalny, nie estetyczny: transkrypt to
 * skonczony dokument czytany od pierwszego zdania, a odtwarzanie nagrania startuje
 * od 0:00 - przy porcji liczonej od konca pierwsza podswietlana wypowiedz nie
 * istnialaby jeszcze w drzewie i auto-scroll nie mialby do czego skoczyc.
 */
export function Rozmowa({
  utterances,
  tintDlaMowcy,
  modelEkspresji,
  zakresy,
  biezacyIndex,
  naPrzewin,
  dlugoscSek,
  pokazCzas = true,
  rodzajMaterialu = "nagranie",
  jakosci,
  pokazLegende = false,
  uwagi,
  skokDo = null,
}: Props): React.JSX.Element {
  const [ile, setIle] = useState(PORCJA);
  /** Domyslnie uwagi sa widoczne: to one tlumacza, skad kolor dymka. Przelacznik stoi w legendzie. */
  const [pokazUwagi, setPokazUwagi] = useState(true);
  const [pozaWidokiem, setPozaWidokiem] = useState(false);
  const [wyroznionyIndex, setWyroznionyIndex] = useState<number | null>(null);
  const [zaznaczenia, setZaznaczenia] = useState<readonly ZakresZaznaczenia[]>(BRAK_ZAZNACZEN);
  const listaRef = useRef<HTMLOListElement>(null);
  const pozaRef = useRef(false);
  /** Token ostatniej prosby o przewiniecie, ktora juz obsluzylismy: rosnaca porcja nie ma przewijac drugi raz. */
  const obsluzonySkokRef = useRef(0);

  const ustawPoza = useCallback((wartosc: boolean) => {
    pozaRef.current = wartosc;
    setPozaWidokiem(wartosc);
  }, []);

  const elementBiezacy = useCallback((): HTMLElement | null => {
    if (biezacyIndex === null) return null;
    return listaRef.current?.querySelector<HTMLElement>(`[data-index="${biezacyIndex}"]`) ?? null;
  }, [biezacyIndex]);

  const przewinDoBiezacej = useCallback(() => {
    const el = elementBiezacy();
    // jsdom nie implementuje scrollIntoView - brak metody nie moze wywrocic widoku.
    if (!el || typeof el.scrollIntoView !== "function") return;
    el.scrollIntoView({ block: "center", behavior: ograniczonyRuch() ? "auto" : "smooth" });
    ustawPoza(false);
  }, [elementBiezacy, ustawPoza]);

  // Odtwarzanie moze wejsc w wypowiedz spoza zaladowanej porcji - wtedy porcja
  // rosnie sama, bo inaczej nie byloby czego podswietlic.
  useEffect(() => {
    if (biezacyIndex === null) return;
    const pozycja = utterances.findIndex((u) => u.index === biezacyIndex);
    if (pozycja >= ile) setIle(Math.ceil((pozycja + 1) / PORCJA) * PORCJA);
  }, [biezacyIndex, utterances, ile]);

  // CV-5: auto-scroll TYLKO wtedy, gdy klient sam nie odjechal. Inaczej wyrywanie
  // widoku spod palca przy kazdej zmianie wypowiedzi.
  useEffect(() => {
    if (biezacyIndex === null || pozaRef.current) return;
    przewinDoBiezacej();
  }, [biezacyIndex, ile, przewinDoBiezacej]);

  useEffect(() => {
    if (biezacyIndex === null) {
      ustawPoza(false);
      return;
    }
    const sprawdz = (): void => {
      const el = elementBiezacy();
      if (!el) return;
      const prostokat = el.getBoundingClientRect();
      ustawPoza(!(prostokat.bottom > 0 && prostokat.top < window.innerHeight));
    };
    window.addEventListener("scroll", sprawdz, { passive: true });
    return () => window.removeEventListener("scroll", sprawdz);
  }, [biezacyIndex, elementBiezacy, ustawPoza]);

  // Prosba z karty oceny: rozszerz porcje do wypowiedzi, przewin do niej, wyroznij ja na chwile i przenies
  // na nia fokus (czytnik ekranu i klawiatura laduja tam, gdzie oko). Wypowiedz poza zaladowana porcja
  // pojawia sie dopiero po rozszerzeniu, wiec efekt uruchamia sie ponownie przy zmianie `ile`.
  useEffect(() => {
    if (!skokDo || skokDo.token === obsluzonySkokRef.current) return;
    const pozycja = utterances.findIndex((u) => u.index === skokDo.index);
    if (pozycja < 0) {
      obsluzonySkokRef.current = skokDo.token;
      return;
    }
    if (pozycja >= ile) {
      setIle(Math.ceil((pozycja + 1) / PORCJA) * PORCJA);
      return;
    }
    const el = listaRef.current?.querySelector<HTMLElement>(`[data-index="${skokDo.index}"]`);
    if (!el) return;

    obsluzonySkokRef.current = skokDo.token;
    const doZaznaczenia = zaznaczeniaWTekscie(utterances[pozycja].text, skokDo.trafienia);
    // Srodkujemy DYMEK, nie cale <li>: ten zawiera tez wskazania, ktore bywaja wyzsze od dymka, wiec srodek <li>
    // wypadalby daleko od tekstu wypowiedzi. Dlugi dymek idzie do poczatku <li>, z numerem i mowca (przewinDoWypowiedzi).
    // Fokus zostaje na <li>. Gdy sa trafienia do zaznaczenia, przewija nastepny efekt, juz do pierwszego z nich
    // (zaznaczenie pojawia sie dopiero po przerysowaniu).
    if (doZaznaczenia.length === 0) przewinDoWypowiedzi(el);
    el.focus({ preventScroll: true });
    setWyroznionyIndex(skokDo.index);
    setZaznaczenia(doZaznaczenia.length > 0 ? doZaznaczenia : BRAK_ZAZNACZEN);
  }, [skokDo, utterances, ile]);

  useEffect(() => {
    if (wyroznionyIndex === null || zaznaczenia.length === 0) return;
    const znak = listaRef.current?.querySelector<HTMLElement>(`[data-index="${wyroznionyIndex}"] .trafienie-slowa`);
    // jsdom nie implementuje scrollIntoView - brak metody nie moze wywrocic widoku.
    if (znak && typeof znak.scrollIntoView === "function") {
      znak.scrollIntoView({ block: "center", behavior: ograniczonyRuch() ? "auto" : "smooth" });
    }
  }, [wyroznionyIndex, zaznaczenia]);

  useEffect(() => {
    if (wyroznionyIndex === null) return;
    const t = setTimeout(() => {
      setWyroznionyIndex(null);
      setZaznaczenia(BRAK_ZAZNACZEN);
    }, CZAS_WYROZNIENIA_MS);
    return () => clearTimeout(t);
  }, [wyroznionyIndex, skokDo?.token]);

  const widoczne = utterances.slice(0, ile);
  const zostalo = utterances.length - widoczne.length;

  return (
    <div className="rozmowa-wrap">
      {pokazLegende && (
        <LegendaJakosci
          przelacznikUwag={uwagi && uwagi.size > 0 ? { pokazane: pokazUwagi, onZmiana: setPokazUwagi } : undefined}
        />
      )}
      <ol className="rozmowa" role="list" ref={listaRef}>
        <li className="msg msg--system">
          {rodzajMaterialu === "tekst"
            ? "Początek rozmowy"
            : dlugoscSek === null
              ? "Początek nagrania"
              : `Nagranie: ${mmss(dlugoscSek * 1000)}`}
        </li>

        {widoczne.map((u, i) => {
          const poprzednia = i > 0 ? widoczne[i - 1] : null;
          const przerwaMs =
            pokazCzas && poprzednia?.endMs != null && u.startMs - poprzednia.endMs >= PROG_PRZERWY_MS
              ? u.startMs - poprzednia.endMs
              : null;

          return (
            <Fragment key={u.index}>
              {przerwaMs !== null && (
                <li className="msg msg--system">{`Przerwa ${Math.round(przerwaMs / 1000)} s`}</li>
              )}
              <Wypowiedz
                wypowiedz={u}
                tint={tintDlaMowcy(u.speaker)}
                zakresy={zakresy.get(u.index) ?? []}
                modelEkspresji={modelEkspresji}
                biezaca={u.index === biezacyIndex}
                naPrzewin={naPrzewin}
                pokazCzas={pokazCzas}
                jakosc={jakosci?.get(u.index)}
                wyrozniona={u.index === wyroznionyIndex}
                zaznaczenia={u.index === wyroznionyIndex ? zaznaczenia : BRAK_ZAZNACZEN}
                uwagi={uwagi?.get(u.index)}
                pokazUwagi={pokazUwagi}
              />
            </Fragment>
          );
        })}
      </ol>

      {zostalo > 0 && (
        <button type="button" className="rozmowa-wiecej" onClick={() => setIle((n) => n + PORCJA)}>
          {`Pokaż dalsze wypowiedzi (zostało ${zostalo})`}
        </button>
      )}

      {pozaWidokiem && (
        <button type="button" className="rozmowa-pigulka" onClick={przewinDoBiezacej}>
          Wróć do bieżącej wypowiedzi
        </button>
      )}
    </div>
  );
}
